import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Upload, Loader2, Pencil, ArrowRight, ZoomIn, ZoomOut, Maximize, ExternalLink, Download, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage, fileUrl, drawingUrl } from "@/lib/api";
import { useBilling, useDeliveryUpdater } from "@/lib/queries";
import { CHANGE_STATUS_LABELS, SPEC_STATUS_LABELS, DRAWING_STATUS_LABELS, INSTALL_STATUS_LABELS, PRODUCTION_STATUS_LABELS, SERVICE_STATUS_LABELS, weekLabel, formatDate, formatDateTime, formatAmount, diffFields, addressLine } from "@/lib/format";
import { StateChip, toneForChangeStatus, toneForSpecStatus, toneForDrawingStatus, toneForServiceStatus, toneForInstallStatus } from "@/components/common/Badges";
import { SpecTable, SpecDiffList, Timeline, EVENT_LABELS, Section, KVList, ActionLink } from "@/components/common/Blocks";
import { MessageWorkSurface, ChangeSurface, ApprovalWaitingSurface, ReleaseReadySurface, SendApprovalSurface, CalmSurface, BasisPanel } from "@/components/delivery/WorkSurfaces";

/* ---------------------------------------------------------------- Oversikt */

const Message = ({ m }) => (
  <div className="py-3" data-testid={`message-${m.id}`}>
    <p className="t-meta"><span className="font-medium text-foreground">{m.sender}</span> · {m.direction === "inn" ? "fra kunde" : m.direction === "ut" ? "til kunde" : "internt"} · {m.channel} · {formatDateTime(m.received_at)}{m.direction === "inn" && !m.handled ? " · ikke vurdert" : ""}</p>
    {m.subject && <p className="mt-1 text-[14.5px] font-medium">{m.subject}</p>}
    <p className="mt-1 whitespace-pre-line text-[14.5px] leading-relaxed">{m.body}</p>
  </div>
);

export const OverviewTab = ({ d, messageId, onRelease, onSendApproval, onAddMessage, onTab }) => {
  const c = d.computed;
  const pend = c.pending_spec_revision;
  const { data: billing } = useBilling(d.id);
  const [allMsgs, setAllMsgs] = useState(false);
  const openChange = d.change_requests.find((x) => !["frigitt", "avvist"].includes(x.status));
  const waiting = d.approval_requests.find((a) => a.status === "venter" && !a.change_request_id);
  const inbound = d.messages.filter((m) => m.direction === "inn").sort((a, b) => b.received_at.localeCompare(a.received_at));
  const unhandled = inbound.filter((m) => !m.handled);
  const chosenMsg = (messageId && unhandled.find((m) => m.id === messageId)) || unhandled.find((m) => !m.change_request_id) || unhandled[0];
  const hasSpec = !["foresporsel", "tilbud"].includes(d.phase);
  const canSendInitial = !openChange && pend && pend.status === "utkast" && !waiting && ["bestilt", "klargjoring"].includes(d.phase);

  // One job at a time: the message first (unless it belongs to the open change), then the change, then approvals.
  let surface;
  if (chosenMsg && !(openChange && chosenMsg.change_request_id === openChange.id)) {
    surface = <MessageWorkSurface key={chosenMsg.id} d={d} messageId={chosenMsg.id} onAddMessage={onAddMessage} />;
  } else if (openChange) {
    surface = <ChangeSurface d={d} cr={openChange} onRelease={onRelease} />;
  } else if (waiting) {
    surface = <ApprovalWaitingSurface ar={waiting} />;
  } else if (pend && pend.status === "kundegodkjent") {
    surface = <ReleaseReadySurface d={d} rev={pend} onRelease={onRelease} />;
  } else if (canSendInitial) {
    surface = <SendApprovalSurface rev={pend} onSend={onSendApproval} />;
  } else {
    surface = <CalmSurface na={c.next_action} />;
  }

  const dialog = d.messages.filter((m) => m.id !== chosenMsg?.id).sort((a, b) => b.received_at.localeCompare(a.received_at));
  const shown = allMsgs ? dialog : dialog.slice(0, 1);

  return (
    <div className="grid grid-cols-1 gap-x-12 gap-y-10 lg:grid-cols-12">
      <div className="space-y-10 lg:col-span-7 xl:col-span-8">
        <div data-testid="current-handling">{surface}</div>

        <Section title="Kundedialog" testId="latest-customer-dialog" right={<button type="button" className="link-quiet" onClick={onAddMessage} data-testid="dialog-add-message">Registrer melding</button>}>
          {dialog.length === 0 ? <p className="t-body text-muted-foreground">Ingen andre meldinger registrert.</p> : (
            <>
              <div className="divide-y">{shown.map((m) => <Message key={m.id} m={m} />)}</div>
              {dialog.length > 1 && (
                <button type="button" className="link-quiet mt-2 text-[13.5px]" onClick={() => setAllMsgs((v) => !v)} data-testid="dialog-toggle-all">{allMsgs ? "Vis færre" : `Vis alle (${dialog.length})`}</button>
              )}
            </>
          )}
        </Section>

        {d.change_requests.length > 0 && (
          <Section title="Endringer" testId="change-requests-section">
            <ul className="divide-y">
              {d.change_requests.map((cr) => (
                <li key={cr.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2.5" data-testid={`change-row-${cr.number}`}>
                  <div className="min-w-0">
                    <Link to={`/leveranser/${d.id}/endring/${cr.id}`} className="link text-[15px] font-medium" data-testid={`open-change-${cr.number}`}>{cr.title}</Link> <span className="t-meta tabular">{cr.number}</span>
                    <p className="t-meta mt-0.5 truncate">{cr.items.map((i) => `${i.label}: ${i.from || "–"} → ${i.to}`).join(" · ")}</p>
                  </div>
                  <StateChip tone={toneForChangeStatus(cr.status)}>{CHANGE_STATUS_LABELS[cr.status]}</StateChip>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {c.service_cases.length > 0 && (
          <Section title="Serviceoppdrag på denne leveransen" testId="delivery-service-cases">
            <ul className="divide-y">
              {c.service_cases.map((s) => (
                <li key={s.id}>
                  <Link to={`/service/${s.id}`} className="list-row list-row-link text-[14.5px]">
                    <span className="min-w-0 flex-1 truncate">{s.title} <span className="t-meta tabular">{s.number}</span></span>
                    <StateChip tone={toneForServiceStatus(s.status)}>{SERVICE_STATUS_LABELS[s.status]}</StateChip>
                  </Link>
                </li>
              ))}
            </ul>
          </Section>
        )}

        <Section title="Siste hendelser" testId="recent-events" right={<button type="button" className="link-quiet" onClick={() => onTab("historikk")}>Hele historikken</button>}>
          <Timeline events={d.events} limit={3} testId="recent-events-timeline" />
        </Section>

        <Section title="Pris og fakturering" testId="billing-section" right={<span className="t-meta">Eks. mva</span>}>
          {!billing ? <p className="t-meta">Henter…</p> : (
            <div className="divide-y border-t text-[14.5px]">
              <div className="flex justify-between gap-3 py-2"><span>{billing.base?.label || "Opprinnelig leveranse"}</span><span className="tabular">{billing.base ? formatAmount(billing.base.amount) : "Ikke prissatt"}</span></div>
              {billing.approved_additions.map((a) => <div key={a.id} className="flex justify-between gap-3 py-2" data-testid="billing-approved-addition"><span>{a.label} <span className="t-meta">· kundegodkjent</span></span><span className="tabular">{formatAmount(a.amount)}</span></div>)}
              {billing.under_assessment.map((a) => <div key={a.id} className="flex justify-between gap-3 py-2 text-muted-foreground" data-testid="billing-under-assessment"><span>{a.label} <span className="t-meta">· under vurdering</span></span><span className="tabular">{formatAmount(a.amount)}</span></div>)}
              {billing.non_billable.map((a) => <div key={a.id} className="flex justify-between gap-3 py-2 text-muted-foreground"><span>{a.label} <span className="t-meta">· ikke fakturerbar</span></span><span className="tabular">–</span></div>)}
              {billing.rejected.map((a) => <div key={a.id} className="flex justify-between gap-3 py-2 text-muted-foreground line-through"><span>{a.label}</span><span className="tabular">{formatAmount(a.amount)}</span></div>)}
              <p className="t-meta py-2">Spesifikasjon rev. {billing.documentation.spec_revision || "–"} · {billing.documentation.drawings.length} gjeldende tegning(er) · {billing.documentation.photo_count} bilder{billing.documentation.report ? " · montasjerapport" : ""}. Ingen faktura sendes fra prototypen.</p>
            </div>
          )}
        </Section>
      </div>

      <div className="lg:col-span-5 xl:col-span-4">
        <BasisPanel d={d} onTab={onTab} />
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------- Spesifikasjon */

export const SpecTab = ({ d }) => {
  const revs = [...d.spec_revisions].reverse();
  const cur = d.computed.current_spec_revision;
  const [selId, setSelId] = useState((cur || revs[0])?.id);
  const sel = d.spec_revisions.find((r) => r.id === selId) || revs[0];
  const base = sel?.based_on ? d.spec_revisions.find((r) => r.id === sel.based_on) : null;
  const diff = useMemo(() => (base && sel ? diffFields(base.fields, sel.fields) : []), [base, sel]);
  const openChange = d.change_requests.find((x) => !["frigitt", "avvist"].includes(x.status));
  if (!sel) return <p className="t-body text-muted-foreground">Ingen spesifikasjon registrert.</p>;
  return (
    <div className="grid grid-cols-1 gap-x-12 gap-y-8 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <Section title="Revisjoner" testId="spec-revisions" description="Eldre revisjoner beholdes og overskrives aldri.">
          <ul className="divide-y border-b">
            {revs.map((r) => {
              const active = r.id === sel.id;
              return (
                <li key={r.id}>
                  <button type="button" onClick={() => setSelId(r.id)} className={`w-full border-l-2 py-2.5 pl-3 pr-2 text-left transition-colors ${active ? "border-primary bg-[hsl(var(--accent)/0.55)]" : "border-transparent hover:bg-secondary"}`} data-testid={`spec-revision-${r.code}`} aria-pressed={active}>
                    <div className="flex items-center justify-between gap-2"><span className="text-[15px] font-semibold">Rev. {r.code}</span><StateChip tone={toneForSpecStatus(r.status)}>{SPEC_STATUS_LABELS[r.status]}</StateChip></div>
                    <p className="t-meta mt-0.5">{formatDate(r.created_at)} · {r.created_by}{r.released_at ? ` · frigitt ${formatDate(r.released_at)}` : r.customer_approved_at ? ` · kundegodkjent ${formatDate(r.customer_approved_at)}` : ""}</p>
                    {r.note && <p className="t-meta mt-0.5">{r.note}</p>}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="mt-3">
            {!openChange && d.phase !== "levert" && !["foresporsel", "tilbud"].includes(d.phase) && <ActionLink to={`/leveranser/${d.id}/endring`} testId="spec-register-change-button"><Pencil className="!h-4 !w-4" /> Registrer endring</ActionLink>}
            {openChange && <ActionLink to={`/leveranser/${d.id}/endring/${openChange.id}`}>Åpne endring {openChange.number}</ActionLink>}
          </div>
        </Section>
      </div>
      <div className="space-y-8 lg:col-span-8">
        {base && (
          <Section title={`Endringer fra rev. ${base.code} til rev. ${sel.code}`} testId="spec-diff-section">
            <SpecDiffList diff={diff} />
          </Section>
        )}
        <Section title={`Spesifikasjon rev. ${sel.code}`} testId="spec-current" right={<StateChip tone={toneForSpecStatus(sel.status)} strong>{SPEC_STATUS_LABELS[sel.status]}</StateChip>}>
          <SpecTable fields={sel.fields} highlightKeys={diff.map((x) => x.key)} />
          <p className="t-meta mt-4">Kundevalg og mål/teknisk grunnlag holdes adskilt. Modellnavn er eksempler.</p>
        </Section>
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------- Dokumenter */

const DOC_KINDS = { tilbud: "Tilbud", ordrebekreftelse: "Ordrebekreftelse", maleskjema: "Måleskjema", rapport: "Montasjerapport", bilde: "Bilde", opplastet: "Dokument", tegning: "Tegning (opplastet)" };
const DRAWING_RELEVANCE = { gjeldende: "Frigitt grunnlag", kundegodkjent: "Kundegodkjent – ikke frigitt", til_godkjenning: "Til kundegodkjenning", utkast: "Utkast", erstattet: "Erstattet", avvist: "Avvist" };
const ZOOMS = [1, 1.5, 2, 3];
const STORAGE_W = "dokumenter:panelbredde";

const UploadPopover = ({ d, onUploaded }) => {
  const update = useDeliveryUpdater();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("opplastet");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);
  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Velg en fil først."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", kind);
      if (note) fd.append("note", note);
      const { data } = await api.post(`/deliveries/${d.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      update(data);
      const added = [...(data.documents || [])].sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
      if (added) onUploaded(`f-${added.id}`);
      toast.success("Dokument lastet opp", { description: "Ligger under Andre dokumenter – ikke som gjeldende grunnlag." });
      setFileName(""); setNote(""); setOpen(false);
    } catch (e) {
      toast.error(errorMessage(e, "Opplasting feilet"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><Button variant="outline" size="sm" className="h-8" data-testid="upload-open-button"><Upload /> Last opp</Button></PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3" data-testid="upload-form">
        <p className="t-meta">PDF, PNG, JPG, WEBP eller SVG, maks 4 MB. Filer i demoøkten utløper automatisk.</p>
        <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 text-[14px] transition-colors hover:bg-secondary">
          <Upload className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className={`truncate ${fileName ? "" : "text-muted-foreground"}`}>{fileName || "Velg fil…"}</span>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.svg" className="sr-only" onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} data-testid="document-upload-input" />
        </label>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="h-10" data-testid="document-kind-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="opplastet">Dokument</SelectItem><SelectItem value="tegning">Tegning</SelectItem><SelectItem value="maleskjema">Måleskjema</SelectItem><SelectItem value="bilde">Bilde</SelectItem>
          </SelectContent>
        </Select>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Merknad (valgfritt)" className="h-10" data-testid="document-note-input" />
        <Button onClick={upload} disabled={busy} className="w-full" data-testid="document-upload-button">{busy ? <Loader2 className="animate-spin" /> : <Upload />} Last opp</Button>
      </PopoverContent>
    </Popover>
  );
};

const DocRow = ({ item, active, onSelect }) => {
  const isDrawing = item.type === "drawing";
  const isSpec = item.type === "spec";
  const title = isDrawing ? item.title : isSpec ? `Spesifikasjon rev. ${item.code}` : item.name;
  const sub = isDrawing ? `Tegning · rev. ${item.code} · ${formatDate(item.created_at)}` : isSpec ? `${SPEC_STATUS_LABELS[item.status]} · ${formatDate(item.created_at)}` : `${DOC_KINDS[item.kind] || item.kind} · ${formatDate(item.created_at)} · ${item.created_by}`;
  const testId = isDrawing ? `view-drawing-${item.code}-${item.kind}` : isSpec ? `view-spec-${item.code}` : `document-row-${item.id}`;
  return (
    <button type="button" className="doc-row" aria-selected={active} onClick={() => onSelect(item.rowId)} data-testid={testId}>
      <div className="min-w-0 flex-1">
        <p className={`truncate text-[14.5px] leading-snug ${active ? "font-semibold" : "font-medium"}`}>{title}</p>
        <p className="t-meta mt-0.5 truncate">{sub}</p>
      </div>
      {isDrawing && item.status !== "gjeldende" && <span className="t-meta shrink-0 pt-0.5">{DRAWING_RELEVANCE[item.status]}</span>}
      {!isDrawing && !isSpec && item.newest && <span className="t-meta shrink-0 pt-0.5">Nyeste</span>}
    </button>
  );
};

const Group = ({ label, items, selId, onSelect, collapsible = false, defaultOpen = true, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!items.length) return null;
  return (
    <div data-testid={testId}>
      {collapsible ? (
        <button type="button" className="doc-group hover:text-foreground" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          <span className="inline-flex items-center gap-1">{open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} {label}</span><span className="tabular">{items.length}</span>
        </button>
      ) : (
        <div className="doc-group"><span>{label}</span><span className="tabular">{items.length}</span></div>
      )}
      {open && items.map((x) => <DocRow key={x.rowId} item={x} active={selId === x.rowId} onSelect={onSelect} />)}
    </div>
  );
};

export const DocumentsTab = ({ d, selectId }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(() => Number(localStorage.getItem(STORAGE_W)) || 300);
  const [dragging, setDragging] = useState(false);
  const [zi, setZi] = useState(0);

  const items = useMemo(() => {
    const drawings = [...d.drawings].sort((a, b) => b.created_at.localeCompare(a.created_at)).map((x) => ({ ...x, type: "drawing", rowId: `d-${x.id}` }));
    const specs = [...d.spec_revisions].sort((a, b) => b.created_at.localeCompare(a.created_at)).map((x) => ({ ...x, type: "spec", rowId: `s-${x.id}` }));
    const docs = [...d.documents].sort((a, b) => b.created_at.localeCompare(a.created_at)).map((x) => ({ ...x, type: "document", rowId: `f-${x.id}` }));
    const newest = docs.find((x) => !x.example);
    if (newest) newest.newest = true;
    return {
      current: [...drawings.filter((x) => x.status === "gjeldende"), ...specs.filter((x) => x.status === "frigitt")],
      pending: [...drawings.filter((x) => ["kundegodkjent", "til_godkjenning", "utkast"].includes(x.status)), ...specs.filter((x) => ["utkast", "til_kundegodkjenning", "kundegodkjent"].includes(x.status))],
      other: docs,
      previous: [...drawings.filter((x) => ["erstattet", "avvist"].includes(x.status)), ...specs.filter((x) => ["erstattet", "avvist"].includes(x.status))],
    };
  }, [d]);
  const all = [...items.current, ...items.pending, ...items.other, ...items.previous];
  const [selId, setSelId] = useState(selectId || all[0]?.rowId || null);
  useEffect(() => { if (selectId) setSelId(selectId); }, [selectId]);
  const selected = all.find((x) => x.rowId === selId) || all[0] || null;
  const select = (id) => { setSelId(id); setZi(0); };

  // Draggable divider between register and viewer.
  const onPointerDown = (e) => { setDragging(true); e.currentTarget.setPointerCapture(e.pointerId); };
  const onPointerMove = (e) => {
    if (!dragging || !containerRef.current) return;
    const left = containerRef.current.getBoundingClientRect().left;
    const w = Math.min(460, Math.max(240, e.clientX - left));
    setWidth(w);
  };
  const onPointerUp = (e) => { setDragging(false); e.currentTarget.releasePointerCapture(e.pointerId); localStorage.setItem(STORAGE_W, String(width)); };

  const isDrawing = selected?.type === "drawing";
  const isSpec = selected?.type === "spec";
  const url = isDrawing ? drawingUrl(selected.id) : selected?.file_id ? fileUrl(selected.file_id) : null;
  const isImage = isDrawing || selected?.content_type?.startsWith("image/");
  const isPdf = selected?.content_type === "application/pdf";
  const zoom = ZOOMS[zi];

  const footer = !selected ? null : isDrawing
    ? `${DRAWING_RELEVANCE[selected.status] || DRAWING_STATUS_LABELS[selected.status]}${selected.approved_at ? ` · godkjent ${formatDate(selected.approved_at)} av ${selected.approved_by}` : ""}${selected.note ? ` · ${selected.note}` : ""}`
    : isSpec
      ? `${SPEC_STATUS_LABELS[selected.status]} · opprettet ${formatDate(selected.created_at)} av ${selected.created_by}${selected.released_at ? ` · frigitt ${formatDate(selected.released_at)}` : ""}${selected.note ? ` · ${selected.note}` : ""}`
      : `${DOC_KINDS[selected.kind] || selected.kind} · ${formatDate(selected.created_at)} · ${selected.created_by}${selected.example ? "" : " · opplastet i demoøkten, utløper automatisk"}${selected.note ? ` · ${selected.note}` : ""}`;

  return (
    <div className="doc-split flex-col lg:flex-row" ref={containerRef} data-testid="document-workspace">
      <div className="doc-panel max-h-[280px] w-full border-b lg:max-h-none lg:w-[var(--w)] lg:border-b-0" style={{ "--w": `${width}px` }} data-testid="document-register">
        <div className="flex h-11 shrink-0 items-center justify-between border-b px-3">
          <p className="text-[14.5px] font-semibold">Dokumenter <span className="t-meta font-normal tabular">{all.length}</span></p>
          <UploadPopover d={d} onUploaded={select} />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto pb-2">
          <Group label="Gjeldende produksjonsgrunnlag" items={items.current} selId={selected?.rowId} onSelect={select} testId="doc-group-current" />
          <Group label="Under behandling" items={items.pending} selId={selected?.rowId} onSelect={select} testId="doc-group-pending" />
          <Group label="Andre dokumenter" items={items.other} selId={selected?.rowId} onSelect={select} testId="doc-group-other" />
          <Group label="Tidligere revisjoner" items={items.previous} selId={selected?.rowId} onSelect={select} collapsible defaultOpen={false} testId="doc-group-previous" />
          {all.length === 0 && <p className="t-meta p-4">Ingen dokumenter.</p>}
        </div>
      </div>

      <div className="doc-resizer hidden lg:block" data-active={dragging} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} role="separator" aria-orientation="vertical" aria-label="Endre bredde på dokumentlisten" data-testid="document-resizer" />

      <div className="flex min-h-[420px] min-w-0 flex-1 flex-col" data-testid="document-viewer">
        {!selected ? (
          <div className="flex flex-1 items-center justify-center t-meta">Velg et dokument i registeret.</div>
        ) : (
          <>
            <div className="flex h-11 shrink-0 items-center justify-between gap-3 border-b px-3">
              <p className="min-w-0 truncate text-[14.5px]" data-testid="document-viewer-title">
                <span className="font-semibold">{isDrawing ? selected.title : isSpec ? "Spesifikasjon" : selected.name}</span>
                {(isDrawing || isSpec) && <span className="text-muted-foreground"> · Rev. {selected.code}</span>}
                {isDrawing && selected.status === "gjeldende" && <StateChip tone="released" className="ml-3" strong>Frigitt grunnlag</StateChip>}
                {isSpec && selected.status === "frigitt" && <StateChip tone="released" className="ml-3" strong>Frigitt – gjeldende</StateChip>}
                {isDrawing && selected.status !== "gjeldende" && <StateChip tone={toneForDrawingStatus(selected.status)} className="ml-3">{DRAWING_RELEVANCE[selected.status]}</StateChip>}
                {isSpec && selected.status !== "frigitt" && <StateChip tone={toneForSpecStatus(selected.status)} className="ml-3">{SPEC_STATUS_LABELS[selected.status]}</StateChip>}
              </p>
              <div className="flex shrink-0 items-center gap-0.5">
                {isImage && (
                  <>
                    <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setZi(0)} disabled={zi === 0} aria-label="Tilpass bredde" data-testid="viewer-fit"><Maximize /> <span className="hidden sm:inline">Tilpass</span></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setZi(Math.max(0, zi - 1))} disabled={zi === 0} aria-label="Zoom ut" data-testid="viewer-zoom-out"><ZoomOut /></Button>
                    <span className="w-11 text-center text-[12.5px] tabular text-muted-foreground">{Math.round(zoom * 100)} %</span>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setZi(Math.min(ZOOMS.length - 1, zi + 1))} disabled={zi === ZOOMS.length - 1} aria-label="Zoom inn" data-testid="viewer-zoom-in"><ZoomIn /></Button>
                  </>
                )}
                {url && (
                  <>
                    <Button variant="ghost" size="sm" className="h-8 px-2" asChild><a href={url} target="_blank" rel="noreferrer" data-testid="viewer-open-new-tab"><ExternalLink /> <span className="hidden sm:inline">Åpne</span></a></Button>
                    <Button variant="ghost" size="sm" className="h-8 px-2" asChild><a href={url} download data-testid="viewer-download"><Download /> <span className="hidden sm:inline">Last ned</span></a></Button>
                  </>
                )}
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-auto bg-white dark:bg-background">
              {isSpec ? (
                <div className="p-5"><SpecTable fields={selected.fields} testId="viewer-spec-table" /></div>
              ) : selected.body ? (
                <pre className="whitespace-pre-wrap p-5 font-mono text-[12.5px] leading-relaxed text-foreground" data-testid="document-body">{selected.body}</pre>
              ) : url && isImage ? (
                <div className="p-4" style={{ width: `${zoom * 100}%` }}><img src={url} alt={selected.title || selected.name} className="block w-full" /></div>
              ) : url && isPdf ? (
                <iframe title={selected.name} src={url} className="h-full min-h-[480px] w-full" />
              ) : url ? (
                <div className="p-6"><Button asChild variant="outline"><a href={url} target="_blank" rel="noreferrer">Åpne fil</a></Button></div>
              ) : (
                <p className="p-6 t-meta">Ingen forhåndsvisning tilgjengelig.</p>
              )}
            </div>
            <p className="t-meta shrink-0 truncate border-t px-3 py-1.5" data-testid="document-viewer-footer">{footer}</p>
          </>
        )}
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------- Montering */

export const InstallationTab = ({ d }) => {
  const inst = d.installation || {};
  const cur = d.computed.current_spec_revision;
  const photos = inst.photos || [];
  return (
    <div className="grid grid-cols-1 gap-x-12 gap-y-8 lg:grid-cols-12">
      <div className="space-y-8 lg:col-span-7">
        <Section title="Montering" testId="installation-section" right={<StateChip tone={toneForInstallStatus(inst.status)} strong>{INSTALL_STATUS_LABELS[inst.status] || "Ikke planlagt"}</StateChip>}>
          <KVList items={[
            ["Planlagt uke", weekLabel(inst.planned_week)],
            ["Dato / tid", inst.planned_date ? `${formatDate(inst.planned_date)}${inst.time_window ? ` · ${inst.time_window}` : ""}` : "Ikke satt"],
            ["Montør", [inst.installer, ...(inst.crew || [])].filter(Boolean).join(", ") || "Ikke satt"],
            ["Kontakt på stedet", inst.site_contact ? `${inst.site_contact.name}${inst.site_contact.phone ? ` · ${inst.site_contact.phone}` : ""}` : d.roles?.site_contact?.name || "–"],
            ["Adresse", addressLine(d.address)],
            ["Produksjon", PRODUCTION_STATUS_LABELS[d.production.status]],
            inst.practical_notes && ["Praktisk", inst.practical_notes],
          ]} />
          <div className="mt-3 flex flex-wrap gap-5">
            <ActionLink to={`/montor/${d.id}`} testId="open-installer-view">Åpne i montørvisning</ActionLink>
            <ActionLink to="/plan">Endre uke i Plan</ActionLink>
          </div>
        </Section>
        <Section title="Montasjerapport" testId="installation-report">
          {!inst.report ? <p className="t-body text-muted-foreground">Ingen rapport registrert. Montøren registrerer resultat, bilder og notat i montørvisningen.</p> : (
            <div className="space-y-4 text-[15px]">
              <p className="t-meta">Lagret {formatDateTime(inst.report.saved_at)} av {inst.report.saved_by}{inst.report.origin?.startsWith("ki") ? " · KI-utkast kontrollert og redigert av montør" : ""}</p>
              <p className="leading-relaxed">{inst.report.summary}</p>
              <div><p className="t-label mb-1">Utført</p><ul className="list-disc space-y-0.5 pl-5">{inst.report.performed.map((p, i) => <li key={i}>{p}</li>)}</ul></div>
              <div><p className="t-label mb-1">Restpunkter</p>{inst.report.remaining.length ? <ul className="list-disc space-y-0.5 pl-5">{inst.report.remaining.map((r, i) => <li key={i}><span className="font-medium">{r.title}</span>{r.description ? ` – ${r.description}` : ""}</li>)}</ul> : <p className="text-muted-foreground">Ingen</p>}</div>
            </div>
          )}
        </Section>
      </div>
      <div className="space-y-8 lg:col-span-5">
        <Section title="Frigitt arbeidsgrunnlag" testId="released-basis">
          {cur ? <p className="t-body">Spesifikasjon rev. {cur.code}, frigitt {formatDate(cur.released_at)} av {cur.released_by}. {d.computed.current_drawings.length} gjeldende tegning(er).</p> : <p className="t-body text-muted-foreground">Ingen frigitt spesifikasjon ennå – montering kan ikke planlegges endelig.</p>}
          {d.computed.pending_spec_revision && <p className="t-meta mt-2">Rev. {d.computed.pending_spec_revision.code} er under behandling og gjelder ikke før den er frigitt.</p>}
        </Section>
        <Section title="Bilder" testId="installation-photos" right={photos.length}>
          {photos.length === 0 ? <p className="t-body text-muted-foreground">Ingen bilder registrert.</p> : (
            <div className="grid grid-cols-3 gap-2">{photos.map((p) => { const pid = p.id || p; return <a key={pid} href={fileUrl(pid)} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-md border"><img src={fileUrl(pid)} alt="Montasjebilde" className="aspect-square w-full object-cover" /></a>; })}</div>
          )}
        </Section>
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------- Historikk */

export const HistoryTab = ({ d }) => {
  const [filter, setFilter] = useState("alle");
  const types = Array.from(new Set(d.events.map((e) => e.type)));
  const events = filter === "alle" ? d.events : d.events.filter((e) => e.type === filter);
  return (
    <Section title="Historikk" testId="history-section" right={`${events.length} hendelser`} description="Alt er lagt til fortløpende – ingenting overskrives.">
      <div className="mb-3 flex flex-wrap gap-1" data-testid="history-filter">
        <Button size="sm" variant={filter === "alle" ? "secondary" : "ghost"} onClick={() => setFilter("alle")}>Alle</Button>
        {types.map((t) => <Button key={t} size="sm" variant={filter === t ? "secondary" : "ghost"} onClick={() => setFilter(t)} data-testid={`history-filter-${t}`}>{EVENT_LABELS[t] || t}</Button>)}
      </div>
      <Timeline events={events} />
    </Section>
  );
};
